/**
 * MarkupTestPage: Test page for markup annotation feature
 * Feature: 049-critique-photo-markup
 *
 * Provides isolated environment for developing and testing markup components
 * before integration with critique system.
 */

import { useState } from 'react';
import { PhotoSelector } from '@/components/markup/PhotoSelector';
import { MarkupEditor } from '@/components/markup/MarkupEditor';
import { MarkupPreview } from '@/components/markup/MarkupPreview';
import { Heading } from '@/components/atoms/Heading';
import { Text } from '@/components/atoms/Text';
import { usePhotosGraphQL } from '@/graphql/hooks/usePhotosGraphQL';
import type { MarkupShape } from '@/types/markup.types';
import logger from '@/utils/logger';

export function MarkupTestPage() {
  const { photos } = usePhotosGraphQL();
  const [selectedPhotoId, setSelectedPhotoId] = useState<string | null>(null);
  const [shapes, setShapes] = useState<MarkupShape[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [commentText, setCommentText] = useState<string>('');
  const [showPreview, setShowPreview] = useState<boolean>(false);

  // Find selected photo
  const selectedPhoto = photos.find((p) => p.id === selectedPhotoId);

  /**
   * Handle photo selection - reset shapes when photo changes
   */
  const handlePhotoSelect = (photoId: string) => {
    logger.info('MarkupTestPage', 'Photo selected:', photoId);
    setSelectedPhotoId(photoId);
    setShapes([]); // Reset shapes for new photo
    setErrorMessage(null);
  };

  /**
   * Handle validation errors from drawing tools
   */
  const handleValidationError = (message: string) => {
    logger.warn('MarkupTestPage', 'Validation error:', message);
    setErrorMessage(message);

    // Auto-clear error after 3 seconds
    setTimeout(() => setErrorMessage(null), 3000);
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="bg-surface-1 border-b border-border p-4">
        <Heading level="h2">Markup Test Page</Heading>
        <Text className="text-muted mt-1">
          Select a photo and use drawing tools to add markup annotations
        </Text>
      </div>

      {/* Error toast */}
      {errorMessage && (
        <div className="bg-danger text-white p-3 text-center border-b border-danger">
          {errorMessage}
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Photo selector sidebar */}
        <div className="w-80 border-r border-border overflow-y-auto bg-surface-1">
          <PhotoSelector selectedPhotoId={selectedPhotoId} onPhotoSelect={handlePhotoSelect} />
        </div>

        {/* Editor area */}
        <div className="flex-1 flex flex-col">
          {selectedPhoto ? (
            <>
              {/* Markup editor (upper section) */}
              <div className="flex-1 min-h-0">
                <MarkupEditor
                  photo={selectedPhoto}
                  shapes={shapes}
                  onShapesChange={setShapes}
                  onValidationError={handleValidationError}
                />
              </div>

              {/* Comment text area (lower section) */}
              <div className="h-64 border-t border-border bg-surface-1 flex flex-col">
                {/* Comment header with preview toggle */}
                <div className="flex items-center justify-between px-4 py-2 border-b border-border">
                  <Text className="font-semibold text-sm">Comment (Test @-mentions)</Text>
                  <button
                    onClick={() => setShowPreview(!showPreview)}
                    className="px-3 py-1 text-sm bg-primary text-white rounded hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={shapes.length === 0}
                    title={shapes.length === 0 ? 'Draw at least one shape to preview' : undefined}
                  >
                    {showPreview ? 'Edit' : 'Preview'}
                  </button>
                </div>

                {/* Comment input or preview */}
                <div className="flex-1 overflow-auto p-4">
                  {showPreview ? (
                    <>
                      {/* Preview mode - show thumbnail that opens modal */}
                      <div className="flex flex-col items-center justify-center gap-4">
                        <MarkupPreview
                          photo={selectedPhoto}
                          shapes={shapes}
                          commentText={commentText}
                        />
                        <Text className="text-sm text-muted text-center">
                          Click thumbnail to view full preview with markup and comment
                        </Text>
                      </div>
                    </>
                  ) : (
                    /* Edit mode - textarea */
                    <textarea
                      value={commentText}
                      onChange={(e) => setCommentText(e.target.value)}
                      placeholder="Write your comment here... Use @1, @2, @3, etc. to reference shapes"
                      className="w-full h-full p-2 bg-surface-2 border border-border rounded text-foreground placeholder:text-muted resize-none focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  )}
                </div>

                {/* Comment help text */}
                {!showPreview && (
                  <div className="px-4 py-2 border-t border-border text-xs text-muted">
                    Tip: Reference shapes by typing @1, @2, @3, etc. Click "Preview" to see styled references.
                    {shapes.length > 0 && ` (${shapes.length} shape${shapes.length !== 1 ? 's' : ''} available to reference)`}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center bg-surface-2">
              <Text className="text-muted">Select a photo to start drawing markup</Text>
            </div>
          )}
        </div>
      </div>

      {/* Instructions footer */}
      <div className="bg-surface-1 border-t border-border p-3">
        <div className="flex gap-8 text-sm text-muted flex-wrap">
          <div>
            <strong>Draw:</strong> Select tool → Click & drag on photo
          </div>
          <div>
            <strong>Select:</strong> Click on shape
          </div>
          <div>
            <strong>Delete:</strong> Select shape → Press Delete/Backspace
          </div>
          <div>
            <strong>Preview:</strong> Type @1, @2 in comment → Click Preview → Click thumbnail
          </div>
          <div>
            <strong>Limit:</strong> Max 9 shapes per photo
          </div>
        </div>
      </div>
    </div>
  );
}
